import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	CaretDownIcon,
	GearSixIcon,
	InfoIcon,
	LifebuoyIcon,
	ListIcon,
} from "@phosphor-icons/react";
import { OnBibleEmblem, OnBibleLogo } from "../../assets/icons";
import { APPLICATION_ROUTES } from "../../utils/routes";
import {
	DesktopSidebarWrapper,
	DesktopSidebarContent,
	DesktopSidebarRailDivider,
	DesktopSidebarTopBar,
	DesktopSidebarScrollable,
	DesktopSidebarNav,
	DesktopSidebarSection,
	DesktopSidebarSectionAction,
	DesktopSidebarSectionHeader,
	DesktopSidebarSectionTitle,
	DesktopSidebarBottom,
	DesktopCollapseButton,
	DesktopSidebarResizeHandle,
} from "./header_styled_components";
import HeaderMenuItem from "./header_menu_item";
import { useSidebarResize } from "../../../hooks/use_sidebar_resize";
import { useAppContext } from "../../../app_context";
import { useSidebarFlag } from "../../../hooks/use_sidebar_flag";
import type { AdminNavItem, MenuItem, ProfileNavItem } from "./nav_item.types";

// The wordmark is never scaled down — shrunk, it got too small to read. Once it no longer
// fits beside the burger (panel under 200px) the box narrows to 34px and clips the
// lettering off: the emblem is the logo's leftmost 34 units, the rail's emblem size.
const PANEL_LOGO_CLASS = [
	"flex shrink-0 overflow-hidden",
	"[&>svg]:h-[34px] [&>svg]:w-[118px] [&>svg]:max-w-none [&>svg]:shrink-0",
	"@max-[200px]:w-[34px]",
].join(" ");

// One 34px-tall box for the rail's emblem and the open logo, so the mark keeps its place.
const LOGO_BOX_CLASS = "flex h-[34px] min-w-0 shrink items-center border-0 bg-transparent p-0";

// 34px is the emblem's size inside the open logo.
const RAIL_EMBLEM_CLASS = "flex [&_svg]:h-[34px] [&_svg]:w-[34px]";

interface Props {
	navItems: MenuItem[];
	profileItems: ProfileNavItem[];
	adminItems: AdminNavItem[];
	unreadMessages: number;
	unreadAnswersCount: number;
	isNavItemDisabled: (menuItem: Pick<MenuItem, "path">) => boolean;
	onOpenSettings: () => void;
	onOpenSupport: () => void;
}

const DesktopSidebar = ({
	navItems,
	profileItems,
	adminItems,
	unreadMessages,
	unreadAnswersCount,
	isNavItemDisabled,
	onOpenSettings,
	onOpenSupport,
}: Props) => {
	const { t } = useTranslation();
	const { isSidebarCollapsed, setIsSidebarCollapsed, setIsAppMenuExpanded, isHeaderMoreMenuOpen } =
		useAppContext();
	const { startResize, toggleWidth } = useSidebarResize();

	const [isSidebarPeeking, setIsSidebarPeeking] = useState(false);
	const [isProfileExpanded, setIsProfileExpanded] = useSidebarFlag("profileExpanded");
	const [isAdminExpanded, setIsAdminExpanded] = useSidebarFlag("adminExpanded");

	const peekOpenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const peekCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const clearPeekTimers = useCallback(() => {
		if (peekOpenTimeoutRef.current) {
			clearTimeout(peekOpenTimeoutRef.current);
			peekOpenTimeoutRef.current = null;
		}
		if (peekCloseTimeoutRef.current) {
			clearTimeout(peekCloseTimeoutRef.current);
			peekCloseTimeoutRef.current = null;
		}
	}, []);

	// Only the emblem opens the peek; hovering the other rail icons must not, otherwise
	// their tooltips never get a chance to show.
	const handleLogoEnter = useCallback(() => {
		if (!isSidebarCollapsed) return;
		clearPeekTimers();
		peekOpenTimeoutRef.current = setTimeout(() => setIsSidebarPeeking(true), 120);
	}, [isSidebarCollapsed, clearPeekTimers]);

	// Passing over the emblem on the way elsewhere must not leave a pending open behind.
	const handleLogoLeave = useCallback(() => {
		if (peekOpenTimeoutRef.current) {
			clearTimeout(peekOpenTimeoutRef.current);
			peekOpenTimeoutRef.current = null;
		}
	}, []);

	const handleSidebarEnter = useCallback(() => {
		if (peekCloseTimeoutRef.current) {
			clearTimeout(peekCloseTimeoutRef.current);
			peekCloseTimeoutRef.current = null;
		}
	}, []);

	const handleSidebarLeave = useCallback(() => {
		if (!isSidebarPeeking) return;
		clearPeekTimers();
		peekCloseTimeoutRef.current = setTimeout(() => setIsSidebarPeeking(false), 150);
	}, [isSidebarPeeking, clearPeekTimers]);

	useEffect(() => clearPeekTimers, [clearPeekTimers]);

	// isSidebarCollapsed can also change from outside (e.g. lesson/notebook editors
	// force-collapse it on mount and restore it on unmount). If it opens mid-peek — or
	// while the 120ms open timer is still pending — the peek flag must not outlive it.
	useEffect(() => {
		if (!isSidebarCollapsed) {
			clearPeekTimers();
			setIsSidebarPeeking(false);
		}
	}, [isSidebarCollapsed, clearPeekTimers]);

	const toggleCollapsed = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			clearPeekTimers();
			setIsSidebarPeeking(false);
			setIsSidebarCollapsed(prev => !prev);
			setIsAppMenuExpanded(true);
		},
		[setIsAppMenuExpanded, setIsSidebarCollapsed, clearPeekTimers],
	);

	const isOpen = !isSidebarCollapsed || isSidebarPeeking;

	const renderItem = (menuItem: MenuItem, unread: number | null = null, admin = false) =>
		isOpen ? (
			<HeaderMenuItem
				key={menuItem.key}
				menuItem={menuItem}
				isHeaderMoreMenuOpen={isHeaderMoreMenuOpen}
				unread={unread}
				showLabelWhenInactive={true}
				fullWidth={true}
				centered={false}
				enableTooltip={false}
				disabled={isNavItemDisabled(menuItem)}
			/>
		) : (
			<HeaderMenuItem
				key={menuItem.key}
				menuItem={menuItem}
				isHeaderMoreMenuOpen={isHeaderMoreMenuOpen}
				unread={unread}
				collapsed
				admin={admin}
				disabled={isNavItemDisabled(menuItem)}
			/>
		);

	// On the rail every item shows regardless of its section being folded: a hairline
	// stands in for the heading, and there is nothing to unfold it with.
	const renderSection = (
		titleKey: string,
		items: MenuItem[],
		isExpanded: boolean,
		toggle: () => void,
		renderOne: (menuItem: MenuItem) => React.ReactNode,
	) => {
		if (!items.length) return null;
		if (!isOpen) {
			return (
				<DesktopSidebarSection $open={false}>
					<DesktopSidebarRailDivider />
					{items.map(renderOne)}
				</DesktopSidebarSection>
			);
		}
		return (
			<DesktopSidebarSection $open>
				<DesktopSidebarSectionHeader type="button" onClick={toggle}>
					<DesktopSidebarSectionTitle>{t(titleKey)}</DesktopSidebarSectionTitle>
					<DesktopSidebarSectionAction $isExpanded={isExpanded}>
						<CaretDownIcon />
					</DesktopSidebarSectionAction>
				</DesktopSidebarSectionHeader>
				{isExpanded && items.map(renderOne)}
			</DesktopSidebarSection>
		);
	};

	const supportItem: MenuItem = {
		key: "sidebar-support",
		icon: LifebuoyIcon,
		textKey: "app-header.sidebar.support",
		lineHeight: "140%",
		onClick: onOpenSupport,
	};

	const settingsItem: MenuItem = {
		key: "sidebar-settings",
		icon: GearSixIcon,
		textKey: "app-header.sidebar.settings",
		lineHeight: "140%",
		onClick: onOpenSettings,
	};

	const aboutItem: MenuItem = {
		key: "sidebar-about",
		path: APPLICATION_ROUTES.ABOUT,
		icon: InfoIcon,
		textKey: "app-header.sidebar.about",
		lineHeight: "140%",
	};

	return (
		<>
			<DesktopSidebarWrapper
				isCollapsed={isSidebarCollapsed}
				isPeeking={isSidebarPeeking}
				onMouseEnter={handleSidebarEnter}
				onMouseLeave={handleSidebarLeave}
				data-testid="header-root"
			>
				<DesktopSidebarContent
					$open={isOpen}
					data-testid={isOpen ? "desktop-sidebar-panel" : "desktop-sidebar-rail"}
				>
					<DesktopSidebarTopBar $open={isOpen}>
						{isSidebarCollapsed ? (
							// The rail's emblem is the logo's own mark at the same spot, so the peek grows the
							// wordmark out of it without anything moving under the cursor. Hover peeks, a click
							// pins the panel open — the designer's call.
							<button
								type="button"
								onClick={toggleCollapsed}
								onMouseEnter={handleLogoEnter}
								onMouseLeave={handleLogoLeave}
								data-testid="hide-header-button"
								aria-label={t("app-header.sidebar.expand")}
								className={LOGO_BOX_CLASS}
							>
								{isOpen ? (
									<span className={PANEL_LOGO_CLASS}>
										<OnBibleLogo />
									</span>
								) : (
									<span className={RAIL_EMBLEM_CLASS}>
										<OnBibleEmblem />
									</span>
								)}
							</button>
						) : (
							// Not a link: the logo sits where the rail's emblem was, so a click aimed at that
							// must not go home.
							<div data-testid="logo-link" className={LOGO_BOX_CLASS}>
								<span className={PANEL_LOGO_CLASS}>
									<OnBibleLogo />
								</span>
							</div>
						)}
						{/* The burger stays at the right; while peeking it pins the panel open too. */}
						{isOpen && (
							<DesktopCollapseButton
								onClick={toggleCollapsed}
								data-testid={isSidebarCollapsed ? undefined : "hide-header-button"}
								aria-label={t(
									isSidebarCollapsed ? "app-header.sidebar.expand" : "app-header.sidebar.collapse",
								)}
							>
								<ListIcon />
							</DesktopCollapseButton>
						)}
					</DesktopSidebarTopBar>

					<DesktopSidebarScrollable $open={isOpen} data-testid="desktop-sidebar-scrollable">
						<DesktopSidebarNav $open={isOpen} data-testid="header-nav">
							{navItems.map(menuItem =>
								renderItem(menuItem, menuItem.key === "chats" ? unreadMessages : null),
							)}
						</DesktopSidebarNav>

						{renderSection(
							"app-header.sidebar.section-profile",
							profileItems,
							isProfileExpanded,
							() => setIsProfileExpanded(prev => !prev),
							menuItem =>
								renderItem(
									menuItem,
									menuItem.key === "profile-questions" ? unreadAnswersCount : null,
								),
						)}

						{renderSection(
							"app-header.sidebar.section-admin",
							adminItems,
							isAdminExpanded,
							() => setIsAdminExpanded(prev => !prev),
							menuItem => renderItem(menuItem, null, true),
						)}
					</DesktopSidebarScrollable>

					<DesktopSidebarBottom $open={isOpen} data-testid="bottom-section">
						{renderItem(supportItem)}
						{renderItem(settingsItem)}
						{renderItem(aboutItem)}
					</DesktopSidebarBottom>
				</DesktopSidebarContent>
			</DesktopSidebarWrapper>

			{/* Outside the aside, so the panel's scrollbar at its right edge stays usable.
			    Dragging the rail's edge right past the threshold opens the panel. Hidden while
			    peeking: that width is borrowed, not the one a drag would set. */}
			{!isSidebarPeeking && (
				<DesktopSidebarResizeHandle
					isCollapsed={isSidebarCollapsed}
					onPointerDown={startResize}
					onDoubleClick={toggleWidth}
				/>
			)}
		</>
	);
};

export default memo(DesktopSidebar);
